import { Link } from "react-router-dom";
import { ArrowRight, Compass, Layers, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const TABLET_IMAGE = "https://images.unsplash.com/photo-1618788372246-79faff0c3742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHN0cmF0ZWd5JTIwcGxhbm5pbmclMjB0YWJsZXQlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc5MDczOTUzM3ww&ixlib=rb-4.1.0&q=85";

export function AboutPage() {
  return (
    <Layout>
      <Seo title="About" description="Learn about Operandi Studio's practical approach to digital products for small-business owners." />
      <section className="texture-noise px-6 pb-24 pt-36 sm:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber-700">About Operandi Studio</p>
            <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-[-0.04em] sm:text-5xl">Useful tools, presented plainly.</h1>
            <p className="mt-6 text-lg leading-9 text-[#525866]">Operandi Studio is being built for owners who want digital products with a clear purpose: reduce repeated work, make customer activity easier to manage, improve marketing consistency, and bring order to daily operations.</p>
            <p className="mt-5 text-base leading-8 text-[#525866]">The storefront avoids invented testimonials, inflated outcomes, and vague promises. Each product should explain what it is, who it is for, what file is delivered, and what it costs before purchase.</p>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="absolute -left-8 -top-8 h-40 w-40 rounded-full bg-amber-500/20 blur-3xl" />
            <img src={TABLET_IMAGE} alt="A minimal business planning workspace with a tablet" className="spotlight-frame relative aspect-[4/5] w-full rounded-[2.5rem] object-cover shadow-[0_30px_90px_rgba(17,19,21,0.16)]" />
          </Reveal>
        </div>
      </section>
      <section className="bg-[#111315] px-6 py-24 text-white">
        <div className="mx-auto grid max-w-7xl gap-5 md:grid-cols-3">
          {[
            [Compass, "Practical before clever", "Products should help a real business task become easier to start, repeat, or delegate."],
            [Layers, "Structured for momentum", "Categories stay focused on time, customers, marketing, and operations so the shop remains easy to browse."],
            [ShieldCheck, "Transparent by default", "Clear pricing, visible policies, secure checkout, and direct support are part of the buying experience."],
          ].map(([Icon, title, text], index) => (
            <Reveal key={title} delay={index * 0.08} className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-8">
              <Icon className="h-7 w-7 text-amber-500" />
              <h2 className="mt-7 font-heading text-2xl font-bold">{title}</h2>
              <p className="mt-4 text-sm leading-7 text-white/62">{text}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="px-6 py-24">
        <Reveal className="mx-auto max-w-4xl rounded-[2.5rem] border border-[#E6E2DA] bg-white p-10 text-center shadow-sm">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight">Have a workflow that needs structure?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[#525866]">Send a note about the business task you are trying to simplify. It may shape a future digital tool.</p>
          <Button asChild className="mt-7 rounded-full bg-[#D97706] text-white hover:bg-[#B45309]" data-testid="about-contact-button">
            <Link to="/contact">Contact the studio <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </Reveal>
      </section>
    </Layout>
  );
}
